import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, FileText } from "lucide-react";
import { api } from "@/lib/api";
import "@/styles/print.css";

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
};

const formatChicagoCitation = (citation, index) => {
  const parts = [];
  
  if (citation.author) parts.push(citation.author);
  if (citation.title) parts.push(`"${citation.title}"`);
  if (citation.publisher) parts.push(citation.publisher);
  if (citation.date_published) parts.push(citation.date_published);
  if (citation.page_or_timecode) parts.push(`p. ${citation.page_or_timecode}`);
  
  return (
    <div key={citation.id} className="print-footnote">
      <span className="print-footnote-num">{index + 1}.</span>{" "}
      {parts.join(", ") || citation.title || "Untitled source"}.
      {citation.url && ` Available at: ${citation.url}`}
    </div>
  );
};

const TicketEntryPrint = ({ entry, citations, artifacts, footnoteOffset = 0 }) => {
  const entryCitations = citations.filter(c => 
    entry.citation_ids?.includes(c.id) || c.claim_id === entry.id
  );
  
  const entryArtifacts = artifacts.filter(a => 
    entry.artifact_ids?.includes(a.id) || a.related_ticket_entries?.includes(entry.id)
  );

  return (
    <div className="print-entry print-no-break">
      <h2 className="print-entry-title">{entry.title}</h2>
      
      <div className="print-narrative">
        {entry.narrative?.split("\n").map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>
      
      {entry.claim_status && (
        <div className="print-status">
          <strong>Claim Status:</strong> {entry.claim_status}
        </div>
      )}
      
      {entryCitations.length > 0 && (
        <div className="print-footnotes">
          <div className="print-footnotes-title">Primary Sources</div>
          {entryCitations.map((citation, i) => 
            formatChicagoCitation(citation, footnoteOffset + i)
          )}
        </div>
      )}
      
      {entryArtifacts.length > 0 && (
        <div className="print-related">
          <div className="print-related-title">Related Artifacts</div>
          <ul className="print-related-list">
            {entryArtifacts.map(artifact => (
              <li key={artifact.id}>
                {artifact.name}
                {artifact.verification_status && ` (${artifact.verification_status})`}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="print-revision">
        Last Updated: {formatDate(entry.updated_at || entry.created_at)}
      </div>
    </div>
  );
};

const PrintTicketsPage = () => {
  const { section } = useParams();
  const [searchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [citations, setCitations] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasPrinted = useRef(false);
  const autoPrint = searchParams.get("auto") === "true";

  const loadData = useCallback(async () => {
    try {
      // Load ticket entries
      const ticketRes = await api.get("/tickets");
      let allEntries = ticketRes.data;
      
      // Filter by section if specified
      if (section) {
        allEntries = allEntries.filter(e => e.section === section);
      }
      
      // Sort by display_order
      allEntries.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setEntries(allEntries);
      
      // Load citations
      const citRes = await api.get("/citations");
      setCitations(citRes.data);
      
      // Load artifacts
      const artRes = await api.get("/artifacts");
      setArtifacts(artRes.data);
      
    } catch (err) {
      setError("Failed to load data");
    }
    setLoading(false);
  }, [section]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    // Auto-print after content loads
    if (!loading && entries.length > 0 && autoPrint && !hasPrinted.current) {
      hasPrinted.current = true;
      setTimeout(() => window.print(), 500);
    }
  }, [loading, entries, autoPrint]);

  const handlePrint = () => {
    window.print();
  };

  // Group entries by section
  const groupedEntries = entries.reduce((acc, entry) => {
    const sec = entry.section || "uncategorized";
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(entry);
    return acc;
  }, {});

  const sectionTitles = {
    concept: "Concept & Planning",
    design_iteration: "Design Iterations",
    proof: "Proofs & Samples",
    negative: "Negatives & Production",
    seating_zone: "Seating Zones",
    print_logic: "Print Logic & Numbering",
    quantities: "Quantities & Distribution",
    distribution: "Distribution Methods",
    charity_link: "Charity Connection",
    myths_vs_facts: "Myths vs. Facts",
    uncategorized: "Other Entries"
  };

  if (loading) {
    return (
      <div className="print-layout">
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-layout">
        <div className="text-center py-20 text-red-600">{error}</div>
      </div>
    );
  }

  const printDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });

  const sourceUrl = window.location.origin + "/tickets" + (section ? `/${section}` : "");

  return (
    <div className="print-layout" data-testid="print-tickets-page">
      {/* Print Controls (hidden in print) */}
      <div className="print-controls no-print">
        <div className="flex items-center gap-4">
          <Link to={section ? `/tickets/${section}` : "/tickets"} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4" />
            Back to Site
          </Link>
          <span className="text-gray-400">|</span>
          <span className="text-sm text-gray-600">
            <FileText className="w-4 h-4 inline mr-1" />
            {entries.length} {entries.length === 1 ? "entry" : "entries"} to print
          </span>
        </div>
        <button onClick={handlePrint} data-testid="print-btn">
          <Printer className="w-4 h-4 inline mr-2" />
          Print Document
        </button>
      </div>
      
      {/* Print Header */}
      <div className="print-header">
        <h1>Elvis Aloha 1973 Historical Archive</h1>
        <div className="print-meta">
          Ticket History Documentation
          <br />
          Printed: {printDate} | Source: {sourceUrl}
        </div>
      </div>
      
      {/* Table of Contents (for multiple sections) */}
      {Object.keys(groupedEntries).length > 1 && (
        <div className="print-toc print-no-break">
          <div className="print-toc-title">Table of Contents</div>
          <ul className="print-toc-list">
            {Object.entries(groupedEntries).map(([sec, items]) => (
              <li key={sec}>
                <span className="toc-title">{sectionTitles[sec] || sec}</span>
                <span className="toc-page">{items.length} {items.length === 1 ? "entry" : "entries"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Content by Section */}
      {Object.entries(groupedEntries).map(([sec, sectionEntries], sectionIndex) => {
        let footnoteOffset = 0;
        // Calculate footnote offset from previous sections
        for (let i = 0; i < sectionIndex; i++) {
          const prevSection = Object.keys(groupedEntries)[i];
          groupedEntries[prevSection].forEach(entry => {
            const entryCitations = citations.filter(c => 
              entry.citation_ids?.includes(c.id) || c.claim_id === entry.id
            );
            footnoteOffset += entryCitations.length;
          });
        }
        
        return (
          <div key={sec} className={sectionIndex > 0 ? "print-page-break" : ""}>
            <div className="print-section-header">
              {sectionTitles[sec] || sec}
            </div>
            
            {sectionEntries.map((entry, entryIndex) => {
              // Calculate footnote offset for this entry
              let entryFootnoteOffset = footnoteOffset;
              for (let i = 0; i < entryIndex; i++) {
                const prevEntry = sectionEntries[i];
                const prevCitations = citations.filter(c => 
                  prevEntry.citation_ids?.includes(c.id) || c.claim_id === prevEntry.id
                );
                entryFootnoteOffset += prevCitations.length;
              }
              
              return (
                <TicketEntryPrint
                  key={entry.id}
                  entry={entry}
                  citations={citations}
                  artifacts={artifacts}
                  footnoteOffset={entryFootnoteOffset}
                />
              );
            })}
          </div>
        );
      })}
      
      {/* Print Footer (shown in print only via CSS) */}
      <div className="print-footer">
        Elvis Aloha 1973 Historical Archive | {sourceUrl}
      </div>
    </div>
  );
};

export default PrintTicketsPage;
